import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PartnershipForm = () => {
  const { t, language } = useLanguage();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    website: '',
    salesChannels: '',
    orderVolume: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_URL}/api/partnership-request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          language: language
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to submit form');
      }

      setIsSuccess(true);
      toast({
        title: t.form.success,
        description: "We'll review your application and get back to you soon.",
      });
      
      setFormData({
        name: '',
        company: '',
        email: '',
        phone: '',
        website: '',
        salesChannels: '',
        orderVolume: '',
        message: ''
      });
    } catch (error) {
      console.error('Form submission error:', error);
      toast({
        title: t.form.error,
        description: "Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-gradient-to-br from-[#1c3855] via-[#2a4a6a] to-[#1c3855] text-white relative overflow-hidden">
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PHBhdGggZD0iTTM2IDM0aDh2OGgtOHptMCAxOGg4djhoLTh6bTAgMThoOHY4aC04em0wIDE4aDh2OGgtOHptMC03Mmg4djhoLTh6bTE4IDBoOHY4aC04em0wIDE4aDh2OGgtOHptMCAxOGg4djhoLTh6bTAgMThoOHY4aC04em0wLTcyaDh2OGgtOHoiLz48L2c+PC9nPjwvc3ZnPg==')] opacity-30"></div>
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-12">
          <h2 className="text-4xl sm:text-5xl font-bold mb-4">
            {t.finalCTA.title}
          </h2>
          <p className="text-xl text-blue-100">
            {t.finalCTA.description}
          </p>
        </div>

        {isSuccess ? (
          <div className="bg-white/10 backdrop-blur-md rounded-3xl p-12 text-center border border-white/20">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-green-500 mb-6">
              <CheckCircle2 className="h-10 w-10 text-white" />
            </div>
            <h3 className="text-3xl font-bold mb-4">{t.form.success}</h3>
            <p className="text-blue-100 text-lg mb-8">
              Our team will review your application and contact you within 24 hours.
            </p>
            <button
              onClick={() => setIsSuccess(false)}
              className="text-white underline hover:no-underline"
            >
              Submit Another Request
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="bg-white/10 backdrop-blur-md rounded-3xl p-8 md:p-12 border border-white/20 shadow-2xl">
            <h3 className="text-2xl font-bold mb-8">{t.form.title}</h3>
            
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-sm font-medium mb-2">
                  {t.form.name} *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-xl bg-white/90 text-gray-900 border border-white/20 focus:outline-none focus:ring-2 focus:ring-white/50"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">
                  {t.form.company} *
                </label>
                <input
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-xl bg-white/90 text-gray-900 border border-white/20 focus:outline-none focus:ring-2 focus:ring-white/50"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-sm font-medium mb-2">
                  {t.form.email} *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-xl bg-white/90 text-gray-900 border border-white/20 focus:outline-none focus:ring-2 focus:ring-white/50"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">
                  {t.form.phone} *
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-xl bg-white/90 text-gray-900 border border-white/20 focus:outline-none focus:ring-2 focus:ring-white/50"
                />
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium mb-2">
                {t.form.website}
              </label>
              <input
                type="url"
                name="website"
                value={formData.website}
                onChange={handleChange}
                placeholder="https://"
                className="w-full px-4 py-3 rounded-xl bg-white/90 text-gray-900 border border-white/20 focus:outline-none focus:ring-2 focus:ring-white/50"
              />
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium mb-2">
                {t.form.salesChannels} *
              </label>
              <input
                type="text"
                name="salesChannels"
                value={formData.salesChannels}
                onChange={handleChange}
                placeholder={t.form.salesChannelsPlaceholder}
                required
                className="w-full px-4 py-3 rounded-xl bg-white/90 text-gray-900 border border-white/20 focus:outline-none focus:ring-2 focus:ring-white/50"
              />
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium mb-2">
                {t.form.orderVolume} *
              </label>
              <input
                type="text"
                name="orderVolume"
                value={formData.orderVolume}
                onChange={handleChange}
                placeholder={t.form.orderVolumePlaceholder}
                required
                className="w-full px-4 py-3 rounded-xl bg-white/90 text-gray-900 border border-white/20 focus:outline-none focus:ring-2 focus:ring-white/50"
              />
            </div>

            <div className="mb-8">
              <label className="block text-sm font-medium mb-2">
                {t.form.message}
              </label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows="4"
                className="w-full px-4 py-3 rounded-xl bg-white/90 text-gray-900 border border-white/20 focus:outline-none focus:ring-2 focus:ring-white/50"
              />
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full group bg-white text-[#1c3855] px-8 py-4 rounded-xl hover:bg-blue-50 transition-all duration-300 font-semibold text-lg shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isSubmitting ? t.form.submitting : t.form.submit}
              {!isSubmitting && (
                <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
              )}
            </button>
          </form>
        )}
      </div>
    </section>
  );
};

export default PartnershipForm;
